import React, { useState, useEffect } from 'react';
import api from '../../api';
import LeagueMetrics from '../LeagueMetrics/LeagueMetrics';
import './Leaderboard.css';

const getWeekDisplay = (week) => {
  return `BW${week.bowlWeekNumber}-${week.pickNumber}`;
};

const getGamesBehindDisplay = (gamesBehind) => {
  if (gamesBehind === undefined || gamesBehind === null) return '-';
  return gamesBehind === 0 ? '-' : gamesBehind.toFixed(1);
};

const getMarginDisplay = (margin) => {
  return margin !== undefined ? margin.toFixed(1) : '0.0';
};

const getWeekStatus = (status) => {
  switch (status?.toLowerCase()) {
    case 'won':
    case 'w':
      return 'W';
    case 'lost':
    case 'l':
      return 'L';
    case 'bye':
    case 'b':
      return 'B';
    case 'pending':
    case 'pm':
      return 'PM';
    case 'winning':
      return "W'ing";
    case 'losing':
      return "L'ing";
    default:
      return '-';
  }
};

const getBestPossibleDisplay = (bestPossible, currentLosses) => {
  if (bestPossible === undefined || bestPossible === null) return '-';
  return `${bestPossible}-${currentLosses}`;
};

const getLocksRemainingDisplay = (locksRemaining, byeUsed) => {
  if (locksRemaining === undefined || locksRemaining === null) return '-';
  return locksRemaining;  // Will now display either "3+Bye" or just "3" depending on bye status
};

const LeagueStats = () => {
  const [activeView, setActiveView] = useState('leaderboard');
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const renderViewToggle = () => (
    <div className="view-toggle">
      <button
        className={`toggle-button ${activeView === 'leaderboard' ? 'active' : ''}`}
        onClick={() => setActiveView('leaderboard')}
      >
        Leaderboard
      </button>
      <button
        className={`toggle-button ${activeView === 'overview' ? 'active' : ''}`}
        onClick={() => setActiveView('overview')}
      >
        League Overview
      </button>
      <button
        className={`toggle-button ${activeView === 'metrics' ? 'active' : ''}`}
        onClick={() => setActiveView('metrics')}
      >
        League Metrics
      </button>
    </div>
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.getLeaderboard();
        setLeaderboardData(response.data);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const renderTable = () => {
    if (!leaderboardData || !Array.isArray(leaderboardData)) {
      console.log('No leaderboard data available:', leaderboardData);
      return null;
    }

    // Add debugging to see what data we're getting
    console.log('Sample player data:', leaderboardData[0]);

    return (
      <table className={`leaderboard-table ${activeView}`}>
        <thead>
          <tr>
            {/* Static columns - always visible */}
            <th className="static-column">RANK</th>
            <th className="static-column">NAME</th>
            <th className="static-column">WINS</th>
            <th className="static-column">LOSSES</th>
            <th className="static-column">BYE USED</th>

            {/* Leaderboard-specific columns - mark as scrollable */}
            <th className="scrollable-column leaderboard-column">WIN %</th>
            <th className="scrollable-column leaderboard-column">GB LEADER</th>
            <th className="scrollable-column leaderboard-column">TOTAL MARGIN</th>
            <th className="scrollable-column leaderboard-column">AVG MARGIN</th>
            <th className="scrollable-column info-column locks-column">
              Locks Left
              {' '}
              <span className="info-icon" title="Number of locks still needed to be made">ⓘ</span>
            </th>
            <th className="scrollable-column info-column locks-column">
              Games Left
              {' '}
              <span className="info-icon" title="Total games left to play this season">ⓘ</span>
            </th>
            <th className="scrollable-column leaderboard-column">BEST POSSIBLE</th>
            <th className="scrollable-column leaderboard-column">FAV</th>
            <th className="scrollable-column leaderboard-column">DOG</th>
            <th className="scrollable-column leaderboard-column">PK</th>
            <th className="scrollable-column leaderboard-column">OVER</th>
            <th className="scrollable-column leaderboard-column">UNDER</th>

            {/* Overview columns - always render */}
            {leaderboardData[0]?.weekStructure?.map((week) => (
              <th
                key={`header-week-${week.weekNumber}-${week.pickNumber}-${week.isBowlWeek ? 'bowl' : 'regular'}`}
                className="scrollable-column overview-column"
              >
                {week.isBowlWeek ? getWeekDisplay(week) : `W${week.weekNumber}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {leaderboardData.map((player, index) => {
            // Calculate win percentage
            const totalGames = (player.wins || 0) + (player.losses || 0);
            const winPct = totalGames > 0 ? (player.wins || 0) / totalGames : 0;

            return (
              <tr key={`${player.username}-${player.rank}`}>
                {/* Static columns */}
                <td className="static-column">{player.rank || '-'}</td>
                <td className="static-column">{player.username}</td>
                <td className="static-column">{player.wins || 0}</td>
                <td className="static-column">{player.losses || 0}</td>
                <td className="static-column">{player.bye_used ? 'Yes' : 'No'}</td>

                {/* Leaderboard columns */}
                <td className="scrollable-column leaderboard-column">
                  {(winPct * 100).toFixed(1)}%
                </td>
                <td className="scrollable-column leaderboard-column">
                  {getGamesBehindDisplay(player?.gamesBehind)}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {getMarginDisplay(player?.accumulated_margin)}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {getMarginDisplay(player?.average_margin)}
                </td>
                <td className="scrollable-column info-column locks-column">
                  {getLocksRemainingDisplay(player.locks_remaining, player.bye_used)}
                </td>
                <td className="scrollable-column info-column locks-column">
                  {player.games_remaining || 0}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {getBestPossibleDisplay(player.best_possible, player.losses)}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {player.fav_pics || 0}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {player.dog_picks || 0}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {player.pk_picks || 0}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {player.over_picks || 0}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {player.under_picks || 0}
                </td>

                {/* Overview columns */}
                {activeView === 'overview' && player.weeks.map((week, weekIndex) => {
                  const weekKey = `${player.username}-week-${weekIndex}-${week.weekNumber || 'undefined'}-${week.pickNumber || 'undefined'}-${week.isBowlWeek ? 'bowl' : 'regular'}`;
                  return (
                    <td
                      key={weekKey}
                      className={`overview-column week-cell ${week.status || 'none'}`}
                    >
                      {getWeekStatus(week.status)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  if (loading) {
    return (
      <div className="leaderboard-page">
        {renderViewToggle()}
        <div className="loading-container">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="leaderboard-page">
        {renderViewToggle()}
        <div className="error-container">{error}</div>
      </div>
    );
  }

  return (
    <div className="leaderboard-page">
      {renderViewToggle()}
      {activeView === 'metrics' ? (
        <div className="metrics-container">
          <LeagueMetrics leaderboardData={leaderboardData} />
        </div>
      ) : (
        <div className="leaderboard-table-wrapper">
          <div className="leaderboard-container">
            {renderTable()}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeagueStats;
